import { Download, Image as LucideImage, Monitor } from 'lucide-react'
import Image from 'next/image'
import { toast } from 'react-toastify'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { playwrightScreenshot } from 'sprites-types/workflow/tools/playwrightScreenshot'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function PlaywrightScreenshotDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(playwrightScreenshot, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const screenshots = Array.isArray(result) ? result : [result]
  const screenshot = screenshots[0] // Display first screenshot, but preserve all data
  const imageUrl = screenshot?.source?.value || ''
  const filename = screenshot?.filename || 'screenshot.png'
  const url = inputs?.url || 'Unknown URL'

  const handleDownload = async () => {
    if (!imageUrl) {
      toast.error('No image URL available')
      return
    }

    try {
      const response = await fetch(imageUrl)
      if (!response.ok) throw new Error('Network response was not ok')

      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)

      const downloadLink = document.createElement('a')
      downloadLink.href = blobUrl
      downloadLink.download = filename
      downloadLink.click()

      window.URL.revokeObjectURL(blobUrl)
      toast.success('Screenshot downloaded')
    } catch (error) {
      console.error('Download failed:', error)
      toast.error('Download failed')
    }
  }

  const contextActions: ContextAction[] = imageUrl
    ? [
        {
          id: 'download',
          label: 'Download screenshot',
          icon: <Download className="h-4 w-4" />,
          onClick: handleDownload,
        },
      ]
    : []

  if (!imageUrl) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <Monitor className="h-5 w-5 text-gray-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-900">Screenshot captured</div>
            <div className="truncate text-xs text-gray-600">{url}</div>
            <div className="mt-1 text-xs text-gray-500">No preview available</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <ContextMenuWrapper actions={contextActions}>
      <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 border-b border-gray-200/50 px-4 py-3">
          <div className="flex-shrink-0">
            <div className="rounded-full bg-blue-100 p-2">
              <LucideImage className="h-4 w-4 text-blue-600" />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-900">Screenshot captured</div>
            <div className="truncate text-xs text-gray-600">{url}</div>
          </div>
        </div>

        <div className="p-4">
          <Image
            src={imageUrl}
            alt="Screenshot"
            width={800}
            height={320}
            className="max-h-80 w-full rounded-lg border border-gray-200 bg-gray-50 object-contain shadow-sm"
            loading="lazy"
          />
        </div>
      </div>
    </ContextMenuWrapper>
  )
}

function downloadPlaywrightScreenshot(nodeExecution: NodeExecution): Promise<void> {
  const schema = getToolResultSchema(playwrightScreenshot, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    console.error('Schema validation failed for download:', parseResult.error)
    return Promise.resolve()
  }

  const result = parseResult.data
  const screenshots = Array.isArray(result) ? result : [result]
  const screenshot = screenshots[0] // Display first screenshot, but preserve all data
  const imageUrl = screenshot?.source?.value || ''
  const filename = screenshot?.filename || 'screenshot.png'

  if (!imageUrl) return Promise.resolve()

  return fetch(imageUrl)
    .then((response) => {
      if (!response.ok) throw new Error('Network response was not ok')
      return response.blob()
    })
    .then((blob) => {
      const blobUrl = window.URL.createObjectURL(blob)
      const downloadLink = document.createElement('a')
      downloadLink.href = blobUrl
      downloadLink.download = filename
      downloadLink.click()
      window.URL.revokeObjectURL(blobUrl)
    })
    .catch((error) => {
      console.error('Download failed:', error)
      toast.error('Download failed')
    })
}

function playwrightScreenshotToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(playwrightScreenshot, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const screenshots = Array.isArray(result) ? result : [result]
  const screenshot = screenshots[0] // Display first screenshot, but preserve all data
  const imageUrl = screenshot?.source?.value || ''
  const url = inputs?.url || 'Unknown URL'

  return `## Screenshot Captured

**URL:** ${url}

${imageUrl ? `![Screenshot](${imageUrl})` : 'Screenshot captured (no preview available)'}

---
`
}

export const PlaywrightScreenshotDisplay = createWorkflowToolComponent(
  PlaywrightScreenshotDisplayComponent,
  downloadPlaywrightScreenshot,
  playwrightScreenshotToMarkdown
)
